import { useEffect, useRef } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';

interface ProcessingStepProps {
  error: string | null;
  onRetry: () => void;
  onChangePaymentMethod: () => void;
}

export function ProcessingStep({ error, onRetry, onChangePaymentMethod }: ProcessingStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  if (error) {
    return (
      <div className="text-center space-y-8">
        <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
          <AlertCircle className="w-10 h-10 text-destructive" aria-hidden="true" />
        </div>

        <div className="space-y-2">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-3xl md:text-4xl focus-visible:outline-none"
          >
            Something went wrong
          </h1>
          <p className="text-lg text-muted-foreground">{error}</p>
        </div>

        <div className="space-y-3">
          <Button onClick={onRetry} className="w-full h-14 text-lg">
            Try Again
          </Button>
          <Button variant="outline" onClick={onChangePaymentMethod} className="w-full h-14 text-lg">
            Change Payment Method
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-8">
      <div
        className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto"
        aria-hidden="true"
      >
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>

      <div role="status">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl focus-visible:outline-none"
        >
          Processing your investment
        </h1>
        <p className="text-lg text-muted-foreground mt-2">This will only take a moment…</p>
      </div>
    </div>
  );
}
